import { useEffect, useRef, useState, type ReactNode } from 'react';
import { QUALITY_INFO, adminBlockReason, type Action, type AdminAction, type ChatMsg, type Color, type GameEvent, type GameResult, type GameState, type MoveQuality, type MoveRecord, type MoveReview } from '@ic/engine';
import { Board } from './Board';
import { AdminPanel, NukeOverlay, type NukePiece } from './AdminPanel';
import { Confetti } from './Confetti';
import { GameRecap, type RecapNext } from './GameRecap';
import { morphTarget, useArrival } from '../lib/transition';
import { Icon } from './Icons';
import { PieceIcon } from './Pieces';
import { settings, useStore } from '../lib/store';
import { bestMove } from '../lib/adviser';

export interface Side { name: string; rating?: number | null; clockMs?: number | null; running?: boolean; online?: boolean }

export function fmtClock(ms: number): string {
  if (ms <= 0) return '0:00';
  const s = ms / 1000;
  if (s < 10) return `0:0${s.toFixed(1)}`;
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  if (m >= 60) return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function resultText(r: GameResult): string {
  if (!r.winner) return `Draw · ${r.reason}`;
  return `${r.winner === 'w' ? 'White' : 'Black'} wins · ${r.reason}`;
}

/** Pieces each side has lost so far (captured, blown up, burned...). */
function losses(initial: GameState, now: GameState, color: Color): { type: string; n: number }[] {
  const count = (s: GameState) => {
    const m = new Map<string, number>();
    for (const p of s.board) if (p && p.color === color) m.set(p.type, (m.get(p.type) ?? 0) + 1);
    return m;
  };
  const a = count(initial);
  const b = count(now);
  const out: { type: string; n: number }[] = [];
  for (const [t, n] of a) if (n > (b.get(t) ?? 0)) out.push({ type: t, n: n - (b.get(t) ?? 0) });
  return out.sort((x, y) => (now.rs.pieces[x.type]?.value ?? 0) - (now.rs.pieces[y.type]?.value ?? 0));
}

function material(s: GameState, color: Color) {
  let v = 0;
  for (const p of s.board) if (p && p.color === color && !s.rs.pieces[p.type]?.royal) v += s.rs.pieces[p.type]?.value ?? 0;
  return v;
}

function PlayerBar({ side, color, state, initial }: { side: Side; color: Color; state: GameState; initial: GameState }) {
  const set = useStore(settings);
  const lost = losses(initial, state, color === 'w' ? 'b' : 'w');
  const diff = material(state, color) - material(state, color === 'w' ? 'b' : 'w');
  const low = side.clockMs != null && side.clockMs < 20000;
  return (
    <div className={`player-bar ${side.running ? 'to-move' : ''}`}>
      <div className={`avatar ${color}`}>{side.name.slice(0, 1).toUpperCase()}</div>
      <div className="player-meta">
        <div className="player-name">
          {side.name}
          {side.rating != null && <span className="rating">({side.rating})</span>}
          {side.online != null && <span className={`dot ${side.online ? 'on' : ''}`} />}
        </div>
        <div className="captured">
          {lost.map(({ type, n }) => Array.from({ length: n }, (_, i) => (
            <span key={type + i} className="cap"><PieceIcon icon={state.rs.pieces[type]?.icon ?? 'pawn'} color={color === 'w' ? 'b' : 'w'} set={set.pieceSet} /></span>
          )))}
          {diff > 0 && <span className="diff">+{diff}</span>}
        </div>
      </div>
      {side.clockMs != null && <div className={`clock ${side.running ? 'running' : ''} ${low ? 'low' : ''}`}>{fmtClock(side.clockMs)}</div>}
    </div>
  );
}

/** Move list; `selected` is the number of moves played in the shown position. */
export function MoveList({ moves, selected, onSelect, reviews }: { moves: MoveRecord[]; selected: number; onSelect?: (n: number) => void; reviews?: Record<number, MoveReview> }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // Scroll only the move list's own box. scrollIntoView would also scroll the page, which on
    // phones yanks the board out of view after every move.
    const cur = ref.current?.querySelector<HTMLElement>('.move.current');
    let box: HTMLElement | null = ref.current;
    while (box && box.scrollHeight <= box.clientHeight && !box.classList.contains('side-panel')) box = box.parentElement;
    if (!cur || !box || box.scrollHeight <= box.clientHeight) return;
    const top = cur.getBoundingClientRect().top - box.getBoundingClientRect().top;
    if (top < 0) box.scrollTop += top;
    else if (top + cur.offsetHeight > box.clientHeight) box.scrollTop += top + cur.offsetHeight - box.clientHeight;
  }, [selected, moves.length]);
  // Group consecutive actions by color (supports several moves per turn).
  const turns: { w: { san: string; n: number }[]; b: { san: string; n: number }[] }[] = [];
  let lastColor: Color | null = null;
  moves.forEach((m, i) => {
    if ((m.color === 'w' && lastColor !== 'w') || !turns.length) turns.push({ w: [], b: [] });
    turns[turns.length - 1][m.color].push({ san: m.san, n: i + 1 });
    lastColor = m.color;
  });
  const cell = (list: { san: string; n: number }[]) => (
    <span className="move-cell">
      {list.map((m) => (
        <button key={m.n} type="button" className={`move ${m.n === selected ? 'current' : ''}`} onClick={() => onSelect?.(m.n)} disabled={!onSelect}>
          {m.san}
          {reviews?.[m.n] && <QualityBadge q={reviews[m.n].quality} />}
        </button>
      ))}
    </span>
  );
  return (
    <div className="move-list" ref={ref}>
      {turns.length === 0 && <div className="muted small move-empty">No moves yet.</div>}
      {turns.map((t, i) => (
        <div key={i} className="move-row">
          <span className="move-no">{i + 1}.</span>
          {cell(t.w)}
          {cell(t.b)}
        </div>
      ))}
    </div>
  );
}

export function Chat({ messages, onSend }: { messages: ChatMsg[]; onSend: (t: string) => void }) {
  const [text, setText] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => { ref.current?.scrollTo({ top: ref.current.scrollHeight }); }, [messages.length]);
  return (
    <div className="chat">
      <div className="chat-log" ref={ref}>
        {messages.length === 0 && <div className="muted small">Say hi to your opponent.</div>}
        {messages.map((m, i) => <div key={i}><b>{m.from}:</b> {m.text}</div>)}
      </div>
      <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) { onSend(text.trim()); setText(''); } }}>
        <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Message…" maxLength={300} />
      </form>
    </div>
  );
}

export interface Position { state: GameState; lastAction: Action | null }

export function QualityBadge({ q }: { q: MoveQuality }) {
  const info = QUALITY_INFO[q];
  return <span className={`q-badge q-${q}`} style={{ background: info.color }} title={info.label}>{info.symbol}</span>;
}

export interface GameViewProps {
  title: string;
  subtitle?: string;
  state: GameState;
  initial: GameState;
  orientation: Color;
  legal: Action[];
  interactive: boolean;
  onAction: (a: Action) => void;
  lastAction: Action | null;
  events: GameEvent[];
  eventsKey: number;
  moves: MoveRecord[];
  /** Earlier positions for reviewing; index n = after n moves. Missing entries cannot be shown. */
  positions?: (Position | undefined)[];
  players: Record<Color, Side>;
  result: GameResult | null;
  controls: ReactNode;
  notice?: ReactNode;
  chat?: { messages: ChatMsg[]; onSend: (t: string) => void };
  onFlip: () => void;
  endActions?: ReactNode;
  /** The viewer won: show confetti and a personal result title. */
  celebrate?: boolean;
  /** Move the bot is currently considering. */
  ghost?: { action: Action; final?: boolean } | null;
  /** Move ratings by move number. */
  reviews?: Record<number, MoveReview>;
  /** Admin tools for this player (only passed for admins). */
  admin?: { color: Color; onAction: (a: AdminAction) => void; /** Online: the server runs the nuke timeline for everybody. */ remoteNuke?: boolean } | null;
  /** A nuke was launched by the server (counts up): play the full animation. */
  nukeSignal?: number;
  /** The side this player has (bot and online games), for the game recap. */
  viewer?: Color | null;
  /** Every event of the game so far, for the recap's highlights. */
  eventLog?: GameEvent[];
  /** Another variant to suggest once the game is over. */
  next?: RecapNext | null;
}

export function GameView(p: GameViewProps) {
  const top: Color = p.orientation === 'w' ? 'b' : 'w';
  const [tab, setTab] = useState<'moves' | 'chat' | 'rules'>('moves');
  const [dismissed, setDismissed] = useState(false);
  const [view, setView] = useState<number | null>(null);
  // Where a piece stands on screen (percent of the board), for the nuke animation.
  const nukePiece = (st: GameState, sq: number, type: string, color: Color): NukePiece[] => {
    const icon = st.rs.pieces[type]?.icon;
    if (!icon) return [];
    const flip = p.orientation === 'b';
    const col = flip ? st.w - 1 - (sq % st.w) : sq % st.w;
    const row = flip ? Math.floor(sq / st.w) : st.h - 1 - Math.floor(sq / st.w);
    return [{ sq, x: ((col + 0.5) / st.w) * 100, y: ((row + 0.5) / st.h) * 100, size: 100 / st.w, icon, color }];
  };
  // The server launched a nuke (online): everybody plays the siren and the flight, the hit follows.
  useEffect(() => {
    if (!p.nukeSignal) return;
    nukeBusy.current = true;
    setNuke({
      run: p.nukeSignal, phase: 'full',
      // Everything on the board is caught except the kings of the side that launched it.
      pieces: p.state.board.flatMap((pc, sq) => (pc ? nukePiece(p.state, sq, pc.type, pc.color) : [])),
    });
  }, [p.nukeSignal]); // eslint-disable-line react-hooks/exhaustive-deps
  const [spawnType, setSpawnType] = useState<string | null>(null);
  const [nuke, setNuke] = useState<{ run: number; phase: 'full' | 'impact'; pieces: NukePiece[] } | null>(null);
  const ownNuke = useRef(false);
  // True from the launch until the animation is over, so the hit is not played a second time.
  const nukeBusy = useRef(false);
  useEffect(() => { nukeBusy.current = !!nuke; }, [nuke]);
  // Someone else's nuke: play the explosion when its event arrives.
  useEffect(() => {
    if (!p.events.some((e) => e.e === 'nuke')) return;
    if (ownNuke.current) { ownNuke.current = false; return; }
    if (nukeBusy.current) return;
    // The board is already empty: rebuild what was there from the destroy events.
    const doomed = p.events.flatMap((e) => (e.e === 'destroy' && e.cause === 'nuke' ? [{ sq: e.sq, type: e.type, color: e.color }] : []));
    setNuke({ run: p.eventsKey, phase: 'impact', pieces: doomed.flatMap((d) => nukePiece(p.state, d.sq, d.type, d.color)) });
  }, [p.eventsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (p.result) setSpawnType(null); }, [p.result]);
  // Admin helpers: show the best move, or play it automatically on every turn of the admin's side.
  const [hint, setHint] = useState<Action | null>(null);
  const [hintBusy, setHintBusy] = useState(false);
  const [auto, setAuto] = useState(false);
  useEffect(() => setHint(null), [p.moves.length]);
  const showBest = async () => {
    setHintBusy(true);
    const a = await bestMove(p.state, 8);
    setHintBusy(false);
    setHint(a);
  };
  useEffect(() => {
    if (!auto || !p.admin || p.result || !p.interactive || nuke || p.state.turn !== p.admin.color || !p.legal.length) return;
    let stale = false;
    const t = setTimeout(async () => {
      const a = await bestMove(p.state, 7);
      if (!stale && a) p.onAction(a);
    }, 500);
    return () => { stale = true; clearTimeout(t); };
  }, [auto, p.state, p.result, p.interactive, p.legal.length, !!nuke]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (p.result) setAuto(false); }, [p.result]);
  const boardMorph = morphTarget(useArrival() === 'board');
  useEffect(() => setDismissed(false), [p.result?.reason]);
  // A new move always brings the board back to the live position.
  useEffect(() => setView(null), [p.moves.length]);

  const live = p.moves.length;
  const canShow = (n: number) => n === live || !!p.positions?.[n];
  const shown = view ?? live;
  const reviewing = view != null && view !== live;
  const pos = reviewing ? p.positions![view] : undefined;
  const boardState = pos?.state ?? p.state;
  // Rating of the move that led to the shown position, pinned to its destination square.
  const shownAction = reviewing ? pos?.lastAction ?? null : p.lastAction;
  const shownReview = p.reviews?.[shown];
  const shownQ = shownReview?.quality;
  const shownQuality = shownQ && shownAction
    ? { sq: shownAction.t === 'move' ? shownAction.to : shownAction.target ?? shownAction.from, q: shownQ, key: `${shown}:${shownQ}` }
    : null;

  const go = (n: number) => {
    n = Math.max(0, Math.min(live, n));
    while (n < live && !canShow(n)) n++;
    setView(n === live ? null : n);
  };
  const firstAvailable = (() => { for (let n = 0; n <= live; n++) if (canShow(n)) return n; return live; })();

  // Arrow keys step through the game (unless typing in a field).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.target as HTMLElement)?.closest('input, textarea, select')) return;
      if (e.key === 'ArrowLeft') { e.preventDefault(); go(shown - 1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); go(shown + 1); }
      else if (e.key === 'Home') go(firstAvailable);
      else if (e.key === 'End') go(live);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const turnLabel = p.state.result ? '' : `${p.state.turn === 'w' ? 'White' : 'Black'} to move${p.state.rs.rules.movesPerTurn > 1 ? ` (${p.state.actionsLeft} left this turn)` : ''}`;
  return (
    <div className="game-layout">
      <Confetti fire={p.celebrate && p.result ? `${p.moves.length}:${p.result.reason}` : null} />
      <div className="board-col">
        <PlayerBar side={p.players[top]} color={top} state={boardState} initial={p.initial} />
        <div className={`board-holder ${reviewing ? 'reviewing' : ''} ${boardMorph.className}`} style={boardMorph.style}>
          <Board
            state={boardState} orientation={p.orientation} legal={reviewing ? [] : p.legal} interactive={p.interactive && !reviewing && !nuke}
            onAction={p.onAction} lastAction={reviewing ? pos?.lastAction ?? null : p.lastAction}
            events={reviewing ? [] : p.events} eventsKey={reviewing ? -1 - shown : p.eventsKey}
            ghost={reviewing ? null : p.ghost ?? (hint ? { action: hint, final: true } : null)}
            quality={shownQuality}
            pick={p.admin && spawnType && !reviewing ? {
              can: (sq) => !adminBlockReason(p.state, p.admin!.color, { kind: 'spawn', type: spawnType, sq }),
              onPick: (sq) => { p.admin!.onAction({ kind: 'spawn', type: spawnType, sq }); setSpawnType(null); },
            } : null}
          />
          {nuke && (
            <NukeOverlay run={nuke.run} phase={nuke.phase} pieces={nuke.pieces}
              onImpact={nuke.phase === 'full' ? () => { ownNuke.current = true; p.admin?.onAction({ kind: 'nuke' }); } : undefined}
              onDone={() => setNuke(null)} />
          )}
          {p.result && !dismissed && !reviewing && !nuke && (
            <div className="result-modal" role="dialog" aria-labelledby="recap-title">
              <GameRecap
                headline={p.celebrate ? 'You won!' : p.viewer && p.result.winner && p.result.winner !== p.viewer ? 'You lost' : p.result.winner ? `${p.result.winner === 'w' ? 'White' : 'Black'} won` : 'Draw'}
                result={p.result} moves={p.moves} reviews={p.reviews} viewer={p.viewer} eventLog={p.eventLog}
                names={{ w: p.players.w.name, b: p.players.b.name }} rulesetName={p.state.rs.name}
                actions={p.endActions} next={p.next} onShow={(n) => go(n)} onDismiss={() => setDismissed(true)}
              />
            </div>
          )}
        </div>
        <PlayerBar side={p.players[p.orientation]} color={p.orientation} state={boardState} initial={p.initial} />
      </div>
      <aside className="side-panel">
        <div className="panel-head">
          <div className="grow">
            <h1 className="panel-title">{p.title}</h1>
            {p.subtitle && <div className="muted small">{p.subtitle}</div>}
          </div>
          <button className="icon-btn" title="Flip board" aria-label="Flip board" onClick={p.onFlip}><Icon name="flip" /></button>
        </div>
        <div className={`status-line ${p.result ? 'over' : ''}`}>
          {!p.result && <span className={`turn-dot ${p.state.turn}`} />}
          {p.result ? resultText(p.result) : turnLabel}
        </div>
        {reviewing && (
          <div className="review-banner">
            <span>Viewing move {shown} of {live}</span>
            <button type="button" className="btn primary sm" onClick={() => setView(null)}>Back to game</button>
          </div>
        )}
        {shownReview && (
          <div className="coach-line" key={`${shown}:${shownReview.quality}`}>
            <QualityBadge q={shownReview.quality} />
            <div className="grow">
              <b style={{ color: QUALITY_INFO[shownReview.quality].color }}>{QUALITY_INFO[shownReview.quality].label}</b>
              <span className="coach-note">{shownReview.note}</span>
            </div>
            <span className="coach-win" title="Winning chances of the side that moved, after this move">{Math.round(shownReview.winPlayed)}%</span>
          </div>
        )}
        {p.admin && !p.result && (
          <AdminPanel state={p.state} color={p.admin.color} picking={spawnType} onPickType={setSpawnType}
            canAdvise={p.interactive && p.state.turn === p.admin.color && p.legal.length > 0} hintBusy={hintBusy} onBest={showBest} auto={auto} onAuto={setAuto}
            onNuke={() => p.admin!.remoteNuke ? p.admin!.onAction({ kind: 'nuke' }) : setNuke({ run: Date.now(), phase: 'full', pieces: p.state.board.flatMap((pc, sq) => (pc && !(pc.color === p.admin!.color && p.state.rs.pieces[pc.type]?.royal) ? nukePiece(p.state, sq, pc.type, pc.color) : [])) })}
            onWin={() => p.admin!.onAction({ kind: 'win' })} />
        )}
        {p.notice}
        <div className="tabs">
          <button className={tab === 'moves' ? 'on' : ''} onClick={() => setTab('moves')}>Moves</button>
          {p.chat && <button className={tab === 'chat' ? 'on' : ''} onClick={() => setTab('chat')}>Chat{p.chat.messages.length ? ` (${p.chat.messages.length})` : ''}</button>}
          <button className={tab === 'rules' ? 'on' : ''} onClick={() => setTab('rules')}>Rules</button>
        </div>
        <div className="tab-body">
          {tab === 'moves' && <MoveList moves={p.moves} selected={shown} onSelect={(n) => go(n)} reviews={p.reviews} />}
          {tab === 'chat' && p.chat && <Chat {...p.chat} />}
          {tab === 'rules' && <RulesSummary state={p.state} />}
        </div>
        <div className="move-nav" role="group" aria-label="Move navigation">
          <button type="button" title="First position (Home)" aria-label="First position" disabled={shown <= firstAvailable} onClick={() => go(firstAvailable)}><Icon name="first" /></button>
          <button type="button" title="Previous move (Left arrow)" aria-label="Previous move" disabled={shown <= firstAvailable} onClick={() => go(shown - 1)}><Icon name="prev" /></button>
          <button type="button" title="Next move (Right arrow)" aria-label="Next move" disabled={shown >= live} onClick={() => go(shown + 1)}><Icon name="next" /></button>
          <button type="button" title="Latest position (End)" aria-label="Latest position" disabled={shown >= live} onClick={() => go(live)}><Icon name="last" /></button>
        </div>
        <div className="controls">{p.controls}</div>
      </aside>
    </div>
  );
}

export function RulesSummary({ state }: { state: GameState }) {
  const r = state.rs.rules;
  const wc = Object.entries(r.winConditions).filter(([, v]) => v).map(([k]) => ({
    checkmate: 'checkmate', captureAllRoyals: 'capture all kings', captureAnyRoyal: 'capture any king', reachSquare: 'reach a target square',
  })[k]);
  return (
    <div className="rules-summary small">
      <div><b>{state.rs.name}</b> · {state.w}×{state.h}</div>
      {state.rs.description && <p className="muted">{state.rs.description}</p>}
      <ul>
        <li>Win: {wc.join(', ') || 'none'}</li>
        <li>Check rule: {r.check ? 'on' : 'off'} · Castling: {r.castling ? 'on' : 'off'} · En passant: {r.enPassant ? 'on' : 'off'}</li>
        <li>Moves per turn: {r.movesPerTurn}</li>
        <li>Promotion: {r.promotion.enabled ? r.promotion.choices.map((c) => state.rs.pieces[c]?.name ?? c).join(', ') : 'off'}</li>
        {r.scorchedEarth && <li>Scorched earth is on</li>}
        <li>Pieces: {Object.values(state.rs.pieces).map((d) => d.name).join(', ')}</li>
      </ul>
    </div>
  );
}
